import { useMutation, useQuery } from "@apollo/client";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  FaArchive,
  FaCopy,
  FaEdit,
  FaPlus,
  FaSave,
  FaTrash,
  FaUndo,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
  ADD_TIP_MENU,
  COPY_TIP_MENU_ITEM,
  DELETE_TIP_MENU,
  DUPLICATE_TIP_MENU,
  SET_ACTIVE_TIP_MENUS,
  UPDATE_TIP_MENU,
} from "../../../queries/mutation/tip_menu";
import { GET_TIP_MENUS, GET_TIP_MENU_OVERLAPS } from "../../../queries/tip_menu";
import { GET_USER_INFO } from "../../../queries/user_queries";
import { TipMenu, TipMenuItem, TipMenuOverlap } from "../../../types/TipMenu";
import LoadingPage from "../../LoadingPage";

// Up to this many menus can be live at once (mirrors util/tipMenus.ts).
const MAX_ACTIVE_MENUS = 2;

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Something went wrong";

const TipMenuEditor = () => {
  const [menus, setMenus] = useState<TipMenu[] | undefined>();
  const [selectedMenu, setSelectedMenu] = useState<TipMenu | undefined>();
  const [menuItems, setMenuItems] = useState<TipMenuItem[] | undefined>();
  const [isEditingMenuName, setIsEditingMenuName] = useState(false);
  const [dirty, setDirty] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    loading,
    data: menuData,
    refetch,
  } = useQuery(GET_TIP_MENUS, {
    variables: { tipMenu: selectedMenu?.id },
  });
  const { data: overlapData, refetch: refetchOverlaps } = useQuery(
    GET_TIP_MENU_OVERLAPS,
  );
  const { data: userData, refetch: refetchUserInfo } = useQuery(GET_USER_INFO);

  const [updateTipMenu, { loading: saving }] = useMutation(UPDATE_TIP_MENU);
  const [createTipMenu] = useMutation(ADD_TIP_MENU);
  const [deleteTipMenu] = useMutation(DELETE_TIP_MENU);
  const [duplicateTipMenu, { loading: duplicating }] =
    useMutation(DUPLICATE_TIP_MENU);
  const [copyTipMenuItem] = useMutation(COPY_TIP_MENU_ITEM);
  const [setActiveTipMenus, { loading: changingActive }] =
    useMutation(SET_ACTIVE_TIP_MENUS);

  const overlaps: TipMenuOverlap[] = overlapData?.getTipMenuOverlaps ?? [];
  // Priority order: the first wins when amounts overlap.
  const activeMenuIds: string[] = userData?.getUserInfo?.activeMenus ?? [];
  const activeMenus = activeMenuIds
    .map((id) => menus?.find((m) => m.id === id))
    .filter((m): m is TipMenu => !!m);
  const isSelectedActive =
    !!selectedMenu && activeMenuIds.includes(selectedMenu.id);

  // Initialize menus and selected menu
  useEffect(() => {
    if (menuData?.getTipMenus) {
      const list: TipMenu[] = menuData.getTipMenus;
      setMenus(list);
      if (!selectedMenu) {
        setSelectedMenu(list[0]);
      } else {
        // Keep the name in sync if it was renamed elsewhere, but don't
        // clobber an edit in progress.
        const fresh = list.find((m) => m.id === selectedMenu.id);
        if (fresh && !isEditingMenuName && fresh.name !== selectedMenu.name) {
          setSelectedMenu(fresh);
        }
      }
    }
  }, [menuData]);

  // Update menu items when menu data changes
  useEffect(() => {
    if (menuData) {
      setMenuItems(menuData.getTipMenuItems || []);
      setDirty(false);
    }
  }, [menuData]);

  // Refetch when selected menu changes
  useEffect(() => {
    if (selectedMenu?.id) {
      refetch();
    }
  }, [selectedMenu?.id]);

  const isLoadingPage =
    menuItems === undefined || menus === undefined || loading;

  const activeItems = (menuItems ?? []).filter((i) => !i.isArchived);
  const archivedItems = (menuItems ?? []).filter((i) => i.isArchived);
  // Within one menu, two items at the same amount can't both be matched.
  const liveAmounts = activeItems
    .filter((i) => i.isActive !== false)
    .map((i) => i.amount);
  const repeatedAmounts = liveAmounts
    .filter((a, idx) => liveAmounts.indexOf(a) !== idx)
    .filter((a, idx, all) => all.indexOf(a) === idx)
    .sort((a, b) => a - b);

  const changeItems = (next: TipMenuItem[]) => {
    setMenuItems(next);
    setDirty(true);
  };

  const addMenuItem = () => {
    if (!isLoadingPage) {
      const item: TipMenuItem = {
        name: "New Menu Item",
        amount: 0,
        id: Math.random().toString(),
        isActive: true,
        isArchived: false,
        bankByDefault: false,
      };
      changeItems([...menuItems, item]);

      // Scroll to bottom after state update
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({
            top: scrollContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  const updateMenuName = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    if (selectedMenu) {
      setSelectedMenu({ ...selectedMenu, name });
      setDirty(true);
    }
  };

  const updateItem = (id: string, patch: Partial<TipMenuItem>) => {
    if (menuItems) {
      changeItems(
        menuItems.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      );
    }
  };

  const deleteMenuItem = (id: string) => {
    if (menuItems) {
      changeItems(menuItems.filter((item) => item.id !== id));
    }
  };

  const saveMenu = async () => {
    if (selectedMenu) {
      const { name, id } = selectedMenu;
      const inputMenuItems = menuItems?.map((m) => ({
        name: m.name,
        amount: m.amount,
        isActive: m.isActive !== false,
        isArchived: !!m.isArchived,
        bankByDefault: !!m.bankByDefault,
      }));

      try {
        await updateTipMenu({
          variables: {
            updateTipMenuInput: {
              name,
              tipMenu: id,
              tipMenuItems: inputMenuItems,
            },
          },
        });
        toast.success("Menu saved successfully!");
        setIsEditingMenuName(false);
        await Promise.all([refetch(), refetchOverlaps()]);
      } catch (error) {
        toast.error(`Failed to save menu: ${errorMessage(error)}`);
        console.error(error);
      }
    }
  };

  const createNewMenu = async () => {
    try {
      const { data } = await createTipMenu({
        variables: {
          createTipMenuInput: {
            name: "New Menu",
          },
        },
      });
      await refetch();
      setSelectedMenu(data.createTipMenu);
      setIsEditingMenuName(true);
      toast.success("New menu created!");
    } catch (error) {
      toast.error(`Failed to create menu: ${errorMessage(error)}`);
      console.error(error);
    }
  };

  const duplicateMenu = async () => {
    if (!selectedMenu) {
      return;
    }
    try {
      const { data } = await duplicateTipMenu({
        variables: { tipMenu: selectedMenu.id },
      });
      await refetch();
      setSelectedMenu(data.duplicateTipMenu);
      setIsEditingMenuName(false);
      toast.success(`Created "${data.duplicateTipMenu.name}"`);
    } catch (error) {
      toast.error(`Failed to duplicate menu: ${errorMessage(error)}`);
      console.error(error);
    }
  };

  const copyItemTo = async (item: TipMenuItem, toTipMenu: string) => {
    const target = menus?.find((m) => m.id === toTipMenu);
    try {
      await copyTipMenuItem({
        variables: {
          input: {
            name: item.name,
            amount: item.amount,
            toTipMenu,
            bankByDefault: !!item.bankByDefault,
          },
        },
      });
      toast.success(`Copied "${item.name}" to ${target?.name ?? "that menu"}`);
      await refetchOverlaps();
    } catch (error) {
      toast.error(errorMessage(error));
      console.error(error);
    }
  };

  const confirmDeleteMenu = () => {
    (
      document.getElementById("delete_menu_modal") as HTMLDialogElement
    )?.showModal();
  };

  const handleDeleteMenu = async () => {
    if (selectedMenu && menus && menus.length > 1) {
      try {
        await deleteTipMenu({
          variables: { tipMenu: selectedMenu.id },
        });

        // Select another menu after deletion
        const remainingMenus = menus.filter((m) => m.id !== selectedMenu.id);
        setSelectedMenu(remainingMenus[0]);

        toast.success("Menu deleted successfully!");
        await Promise.all([refetch(), refetchUserInfo(), refetchOverlaps()]);
        (
          document.getElementById("delete_menu_modal") as HTMLDialogElement
        )?.close();
      } catch (error) {
        toast.error(`Failed to delete menu: ${errorMessage(error)}`);
        console.error(error);
      }
    }
  };

  const onSelectMenu = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    const menu = menus?.find((m) => m.id === id);
    if (menu) {
      if (
        dirty &&
        !window.confirm("You have unsaved changes on this menu. Leave anyway?")
      ) {
        return;
      }
      setSelectedMenu(menu);
      setIsEditingMenuName(false);
    }
  };

  const setActive = async (ids: string[], success: string) => {
    try {
      await setActiveTipMenus({ variables: { tipMenus: ids } });
      const [, overlapResult] = await Promise.all([
        refetchUserInfo(),
        refetchOverlaps(),
        refetch(),
      ]);
      toast.success(success);
      const fresh: TipMenuOverlap[] =
        overlapResult.data?.getTipMenuOverlaps ?? [];
      if (fresh.length) {
        toast.warn(
          `Both active menus have items at ${fresh
            .map((o) => o.amount)
            .join(", ")} tokens - see the warning above the menu.`,
        );
      }
    } catch (error) {
      toast.error(errorMessage(error));
      console.error(error);
    }
  };

  const activateSelected = () => {
    if (selectedMenu) {
      setActive(
        [...activeMenuIds, selectedMenu.id],
        `"${selectedMenu.name}" is now active`,
      );
    }
  };

  const deactivateSelected = () => {
    if (selectedMenu) {
      setActive(
        activeMenuIds.filter((id) => id !== selectedMenu.id),
        `"${selectedMenu.name}" is no longer active`,
      );
    }
  };

  if (isLoadingPage) {
    return <LoadingPage />;
  }

  const otherMenus = (menus ?? []).filter((m) => m.id !== selectedMenu?.id);

  return (
    <div className="min-h-screen bg-base-200 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="bg-base-100 rounded-lg shadow-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex-1 min-w-[12rem]">
              <h1 className="text-3xl font-bold text-primary mb-2">
                CB Tip Menu
              </h1>
              <p className="text-base-content/70">
                What a Chaturbate tip buys on stream, by token amount; this is
                what the tip queue and the OBS widget match tips against. Up
                to {MAX_ACTIVE_MENUS} menus can be active at once. For
                crimcoin redeems, use the Offline Tip Menu.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto items-center flex-wrap">
              <select
                className="select select-bordered select-primary w-full sm:w-64"
                value={selectedMenu?.id}
                onChange={onSelectMenu}
              >
                {menus?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.isActive ? "🟢 " : ""}
                    {m.name}
                  </option>
                ))}
              </select>

              <button className="btn btn-primary" onClick={createNewMenu}>
                <FaPlus /> New Menu
              </button>

              <button
                className="btn btn-outline"
                onClick={duplicateMenu}
                disabled={duplicating}
                title="Make a copy of this menu and all its items"
              >
                <FaCopy /> {duplicating ? "Copying..." : "Duplicate Menu"}
              </button>

              {isSelectedActive ? (
                <div className="flex items-center gap-2">
                  <span className="badge badge-success badge-lg">
                    ✅ Active
                    {activeMenus.length > 1 &&
                      ` (${activeMenuIds.indexOf(selectedMenu!.id) + 1} of ${activeMenus.length})`}
                  </span>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={deactivateSelected}
                    disabled={changingActive}
                  >
                    Deactivate
                  </button>
                </div>
              ) : (
                <div
                  className="tooltip"
                  data-tip={
                    activeMenuIds.length >= MAX_ACTIVE_MENUS
                      ? `${MAX_ACTIVE_MENUS} menus are already active - deactivate one first`
                      : undefined
                  }
                >
                  <button
                    className="btn btn-success btn-sm"
                    onClick={activateSelected}
                    disabled={
                      changingActive ||
                      activeMenuIds.length >= MAX_ACTIVE_MENUS
                    }
                  >
                    {changingActive ? (
                      <div className="loading loading-spinner loading-xs" />
                    ) : (
                      "🟢 Set as Active"
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {activeMenus.length > 0 && (
            <p className="text-sm text-base-content/70 mt-3">
              Active now:{" "}
              {activeMenus.map((m, i) => (
                <span key={m.id}>
                  {i > 0 && " + "}
                  <span className="font-semibold">{m.name}</span>
                </span>
              ))}
              {activeMenus.length > 1 &&
                " - tippers see both menus combined."}
            </p>
          )}

          {overlaps.length > 0 && (
            <div className="alert alert-warning mt-4">
              <div>
                <div className="font-bold">
                  Your active menus overlap on{" "}
                  {overlaps.length === 1
                    ? "one token amount"
                    : `${overlaps.length} token amounts`}
                </div>
                <ul className="text-sm mt-1 list-disc list-inside">
                  {overlaps.map((o) => (
                    <li key={o.amount}>
                      A tip of <span className="font-semibold">{o.amount}</span>{" "}
                      tokens counts as "{o.keptItem}" ({o.keptMenu}); "
                      {o.droppedItem}" ({o.droppedMenu}) is ignored.
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Menu Name Editor */}
          <div className="divider divider-primary"></div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex-1 w-full">
              <label className="label">
                <span className="label-text font-semibold">Menu Name</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={selectedMenu?.name || ""}
                  onChange={updateMenuName}
                  placeholder="Enter menu name"
                  className="input input-bordered w-full"
                  disabled={!isEditingMenuName && selectedMenu?.name !== ""}
                />
                <button
                  className="btn btn-ghost btn-square"
                  onClick={() => setIsEditingMenuName(!isEditingMenuName)}
                  aria-label="Rename menu"
                >
                  <FaEdit className="text-lg" />
                </button>
              </div>
            </div>

            <div className="flex gap-2 mt-8 items-center">
              {dirty && (
                <span className="badge badge-warning">Unsaved changes</span>
              )}
              <button
                className="btn btn-success"
                onClick={saveMenu}
                disabled={saving}
              >
                <FaSave /> {saving ? "Saving..." : "Save Menu"}
              </button>

              {menus && menus.length > 1 && (
                <button className="btn btn-error" onClick={confirmDeleteMenu}>
                  <FaTrash /> Delete Menu
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Menu Items Section */}
        <div className="bg-base-100 rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-2xl font-bold">
              Menu Items ({activeItems.length})
            </h2>
            <button className="btn btn-primary btn-sm" onClick={addMenuItem}>
              <FaPlus /> Add Item
            </button>
          </div>
          <p className="text-sm text-base-content/60 mb-4">
            Switch an item off to keep it on the menu without matching tips.
            Archive it to move it out of the way; archived items can come
            back any time. Changes here are kept when you save the menu.
          </p>

          {repeatedAmounts.length > 0 && (
            <div className="alert alert-warning mb-4 text-sm">
              <span>
                This menu has more than one active item at{" "}
                {repeatedAmounts.join(", ")} tokens. Only the first one can
                match a tip - switch off or archive the others.
              </span>
            </div>
          )}

          {activeItems.length > 0 ? (
            <div
              ref={scrollContainerRef}
              className="max-h-[calc(100vh-400px)] overflow-y-auto pr-2"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {activeItems.map((item, index) => (
                  <div
                    key={item.id}
                    className={`card bg-base-200 border-2 border-base-300 hover:border-primary transition-all ${
                      item.isActive === false ? "opacity-60" : ""
                    }`}
                  >
                    <div className="card-body p-4">
                      <div className="flex justify-between items-start mb-3">
                        <span className="badge badge-primary badge-lg">
                          #{index + 1}
                        </span>
                        <div className="flex gap-1">
                          <button
                            className="btn btn-ghost btn-sm btn-square"
                            onClick={() =>
                              updateItem(item.id, { isArchived: true })
                            }
                            title="Archive"
                            aria-label={`Archive ${item.name}`}
                          >
                            <FaArchive />
                          </button>
                          <button
                            className="btn btn-ghost btn-sm btn-square text-error"
                            onClick={() => deleteMenuItem(item.id)}
                            title="Delete"
                            aria-label={`Delete ${item.name}`}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>

                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-semibold">Name</span>
                        </label>
                        <input
                          type="text"
                          value={item.name}
                          onChange={(e) =>
                            updateItem(item.id, { name: e.target.value })
                          }
                          placeholder="Item name"
                          className="input input-bordered input-sm w-full"
                        />
                      </div>

                      <div className="form-control">
                        <label className="label">
                          <span className="label-text font-semibold">
                            Tokens
                          </span>
                        </label>
                        <input
                          type="number"
                          value={item.amount}
                          onChange={(e) =>
                            updateItem(item.id, {
                              amount: parseInt(e.target.value) || 0,
                            })
                          }
                          placeholder="0"
                          className="input input-bordered input-sm w-full"
                          min="0"
                        />
                      </div>

                      <div className="flex items-center justify-between gap-2 mt-3">
                        <label className="label cursor-pointer gap-2 p-0">
                          <input
                            type="checkbox"
                            className="toggle toggle-success toggle-sm"
                            checked={item.isActive !== false}
                            onChange={(e) =>
                              updateItem(item.id, {
                                isActive: e.target.checked,
                              })
                            }
                            aria-label={`${item.name} active`}
                          />
                          <span className="label-text text-sm">
                            {item.isActive === false ? "Off" : "Active"}
                          </span>
                        </label>
                        <label
                          className="label cursor-pointer gap-2 p-0"
                          title="Tips for this item go straight to the Banked list on the Tip Queue - for things handled later, so a mod doesn't have to bank each one."
                        >
                          <input
                            type="checkbox"
                            className="toggle toggle-accent toggle-sm"
                            checked={!!item.bankByDefault}
                            onChange={(e) =>
                              updateItem(item.id, {
                                bankByDefault: e.target.checked,
                              })
                            }
                            aria-label={`${item.name} banks by default`}
                          />
                          <span className="label-text text-sm">Auto-bank</span>
                        </label>
                        {otherMenus.length > 0 && (
                          <select
                            className="select select-bordered select-xs max-w-[9rem]"
                            value=""
                            onChange={(e) => {
                              if (e.target.value) {
                                copyItemTo(item, e.target.value);
                              }
                            }}
                            aria-label={`Copy ${item.name} to another menu`}
                          >
                            <option value="">Copy to...</option>
                            {otherMenus.map((m) => (
                              <option key={m.id} value={m.id}>
                                {m.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-base-content/50 text-lg mb-4">
                No menu items yet
              </p>
              <button className="btn btn-primary" onClick={addMenuItem}>
                <FaPlus /> Add Your First Item
              </button>
            </div>
          )}

          {archivedItems.length > 0 && (
            <details className="collapse collapse-arrow bg-base-200 mt-6">
              <summary className="collapse-title font-semibold">
                Archived ({archivedItems.length})
              </summary>
              <div className="collapse-content">
                <ul className="divide-y divide-base-300">
                  {archivedItems.map((item) => (
                    <li
                      key={item.id}
                      className="flex items-center justify-between gap-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="font-medium truncate">{item.name}</div>
                        <div className="text-sm text-base-content/60">
                          {item.amount} tokens
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() =>
                            updateItem(item.id, { isArchived: false })
                          }
                        >
                          <FaUndo /> Unarchive
                        </button>
                        <button
                          className="btn btn-ghost btn-sm btn-square text-error"
                          onClick={() => deleteMenuItem(item.id)}
                          title="Delete"
                          aria-label={`Delete ${item.name}`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          )}
        </div>

        {/* Delete Menu Confirmation Modal */}
        <dialog id="delete_menu_modal" className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Delete Menu?</h3>
            <p className="py-4">
              Are you sure you want to delete "{selectedMenu?.name}"? This
              action cannot be undone.
              {isSelectedActive && " It is active right now."}
            </p>
            <div className="modal-action">
              <form method="dialog" className="flex gap-2">
                <button className="btn">Cancel</button>
                <button
                  className="btn btn-error"
                  onClick={handleDeleteMenu}
                  type="button"
                >
                  Delete
                </button>
              </form>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button>close</button>
          </form>
        </dialog>
      </div>
    </div>
  );
};

export default TipMenuEditor;
